import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import esES from 'date-fns/locale/es';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  MenuItem, 
  TextField, 
  Grid, 
  Alert, 
  Box,
  ToggleButtonGroup,
  ToggleButton,
  Chip
} from '@mui/material';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

const locales = { 'es': esES };
const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Colores por tipo de mantenimiento
const COLORES_MANTENIMIENTO = {
  correctivo: '#dc2626', // Rojo
  preventivo: '#2563eb', // Azul
  predictivo: '#16a34a'  // Verde
};

const CalendarioMantenimiento = () => {
  const [events, setEvents] = useState([]);
  const [maquinas, setMaquinas] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    machineNumber: '',
    tipoMantenimiento: 'preventivo',
    descripcion: '',
    tecnicoAsignado: '',
    observaciones: '',
    estado: 'programado',
    fechaInicio: format(new Date(), 'yyyy-MM-dd'),
    horaInicio: '08:00',
    fechaFin: format(new Date(), 'yyyy-MM-dd'),
    horaFin: '12:00'
  });
  const [editingEvent, setEditingEvent] = useState(null);
  const [error, setError] = useState(null);
  const [date, setDate] = useState(new Date());

  // Función para verificar conflictos de horarios
  const checkEventConflict = useCallback((newEvent) => {
    const { machineNumber, fechaInicio, fechaFin, id } = newEvent;
    const newStart = new Date(fechaInicio);
    const newEnd = new Date(fechaFin);

    // Normalizar machineNumber para comparación
    const normalizedNewMachine = String(machineNumber).toLowerCase().replace(/^machine/, '');

    const conflictingEvents = events.filter(event => {
      if (id && event.id === id) return false;
      
      // Normalizar machineNumber del evento para comparación
      const normalizedEventMachine = String(event.raw.machineNumber).toLowerCase().replace(/^machine/, '');
      if (normalizedEventMachine !== normalizedNewMachine) return false;
      
      if (event.raw.estado === 'cancelado' || event.raw.estado === 'completado') return false;

      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end);

      return (newStart < eventEnd && newEnd > eventStart);
    });

    return conflictingEvents;
  }, [events]);

  // Cargar datos iniciales
  const fetchInitialData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      const [machinesRes, mantenimientosRes] = await Promise.all([
        fetch('/api/machines', { headers }),
        fetch('/api/mantenimiento', { headers })
      ]);
      const [machinesData, mantenimientosData] = await Promise.all([
        machinesRes.json(),
        mantenimientosRes.json()
      ]);
      
      setMaquinas(Array.isArray(machinesData) ? machinesData : []);
      
      // Mapear mantenimientos a eventos del calendario
      const mappedEvents = (Array.isArray(mantenimientosData) ? mantenimientosData : []).map(mant => {
        const start = new Date(mant.fechaInicio);
        const end = new Date(mant.fechaFin);
        const horaInicio = format(start, 'HH:mm');
        const horaFin = format(end, 'HH:mm');
        
        const tipoIcono = mant.tipoMantenimiento === 'correctivo' ? '🔧' : 
                         mant.tipoMantenimiento === 'preventivo' ? '⚙️' : '📊';
        
        return {
          id: mant._id,
          title: `${tipoIcono} M${mant.machineNumber} | ${mant.tipoMantenimiento.toUpperCase()} | ${horaInicio}-${horaFin}`,
          start,
          end,
          raw: mant,
          color: COLORES_MANTENIMIENTO[mant.tipoMantenimiento] || '#6b7280',
          allDay: false
        };
      });
      setEvents(mappedEvents);
    } catch (err) {
      setError("No se pudieron cargar los datos iniciales.");
    }
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  const handleSelectSlot = ({ start, end }) => {
    const fechaInicio = format(start, 'yyyy-MM-dd');
    const horaInicio = format(start, 'HH:mm');
    const fechaFin = format(end, 'yyyy-MM-dd');
    const horaFin = format(end, 'HH:mm');

    setForm({
      machineNumber: '',
      tipoMantenimiento: 'preventivo',
      descripcion: '',
      tecnicoAsignado: '',
      observaciones: '',
      estado: 'programado',
      fechaInicio,
      horaInicio,
      fechaFin,
      horaFin
    });
    setEditingEvent(null);
    setError(null);
    setOpen(true);
  };

  const handleSelectEvent = (event) => {
    const fechaInicio = format(new Date(event.raw.fechaInicio), 'yyyy-MM-dd');
    const horaInicio = format(new Date(event.raw.fechaInicio), 'HH:mm');
    const fechaFin = format(new Date(event.raw.fechaFin), 'yyyy-MM-dd');
    const horaFin = format(new Date(event.raw.fechaFin), 'HH:mm');
    
    setForm({
      machineNumber: event.raw.machineNumber,
      tipoMantenimiento: event.raw.tipoMantenimiento,
      descripcion: event.raw.descripcion,
      tecnicoAsignado: event.raw.tecnicoAsignado || '',
      observaciones: event.raw.observaciones || '',
      estado: event.raw.estado || 'programado',
      fechaInicio,
      horaInicio,
      fechaFin,
      horaFin
    });
    setEditingEvent(event);
    setError(null);
    setOpen(true);
  };

  const handleChange = (field, value) => {
    setForm({ ...form, [field]: value });
    
    if (['machineNumber', 'fechaInicio', 'fechaFin', 'horaInicio', 'horaFin'].includes(field)) {
      setError(null);
    }
  };

  const handleBuscarFecha = (fecha) => {
    setDate(fecha);
  };

  const handleSave = async () => {
    setError(null);
    if (!form.machineNumber || !form.tipoMantenimiento || !form.descripcion || !form.fechaInicio || !form.fechaFin || !form.horaInicio || !form.horaFin) {
      setError("Completa todos los campos obligatorios.");
      return;
    }

    const fechaInicio = `${form.fechaInicio}T${form.horaInicio}:00`;
    const fechaFin = `${form.fechaFin}T${form.horaFin}:00`;

    if (new Date(fechaFin) <= new Date(fechaInicio)) {
      setError("La fecha de fin debe ser posterior a la fecha de inicio.");
      return;
    }

    // Verificar conflictos solo si no está cancelado o completado
    if (form.estado !== 'cancelado' && form.estado !== 'completado') {
      const conflicts = checkEventConflict({
        machineNumber: form.machineNumber,
        fechaInicio,
        fechaFin,
        id: editingEvent?.id
      });

      if (conflicts.length > 0) {
        const conflictInfo = conflicts.map(c => {
          const start = format(new Date(c.start), 'dd/MM HH:mm');
          const end = format(new Date(c.end), 'HH:mm');
          return `${c.raw.tipoMantenimiento} (${start}-${end})`;
        }).join(', ');
        
        setError(`Conflicto de horarios con: ${conflictInfo}`);
        return;
      }
    }

    const datos = {
      machineNumber: form.machineNumber,
      tipoMantenimiento: form.tipoMantenimiento,
      descripcion: form.descripcion,
      tecnicoAsignado: form.tecnicoAsignado,
      observaciones: form.observaciones,
      estado: form.estado,
      fechaInicio,
      fechaFin
    };

    const token = localStorage.getItem('token');
    const headers = {
      'Content-Type': 'application/json',
      ...(token && { 'Authorization': `Bearer ${token}` })
    };

    try {
      let response;
      if (editingEvent) {
        response = await fetch(`/api/mantenimiento/${editingEvent.id}`, {
          method: 'PUT',
          headers,
          body: JSON.stringify(datos)
        });
      } else {
        response = await fetch('/api/mantenimiento', {
          method: 'POST',
          headers,
          body: JSON.stringify(datos)
        });
      }

      if (response.ok) {
        setOpen(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al guardar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor.");
    }
  };

  const handleDelete = async () => {
    setError(null);
    if (!editingEvent) return;

    const token = localStorage.getItem('token');
    const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

    try {
      const response = await fetch(`/api/mantenimiento/${editingEvent.id}`, {
        method: 'DELETE',
        headers
      });
      if (response.ok) {
        setOpen(false);
        fetchInitialData();
      } else {
        const errorData = await response.json();
        setError(`Error al eliminar: ${errorData.message || 'Error desconocido'}`);
      }
    } catch (err) {
      setError("Error de conexión al servidor al eliminar.");
    }
  };

  const getEstadoChipColor = (estado) => {
    switch(estado) {
      case 'programado': return 'primary';
      case 'en_proceso': return 'warning';
      case 'completado': return 'success';
      case 'cancelado': return 'error';
      default: return 'default';
    }
  };

  return (
    <div style={{ height: 900, margin: 20 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <TextField
          label="Buscar fecha"
          type="date"
          onChange={(e) => handleBuscarFecha(new Date(e.target.value))}
          InputLabelProps={{ shrink: true }}
        />
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Chip icon={<BuildIcon />} label="Correctivo" sx={{ bgcolor: COLORES_MANTENIMIENTO.correctivo, color: 'white' }} />
          <Chip icon={<WarningIcon />} label="Preventivo" sx={{ bgcolor: COLORES_MANTENIMIENTO.preventivo, color: 'white' }} />
          <Chip icon={<TrendingUpIcon />} label="Predictivo" sx={{ bgcolor: COLORES_MANTENIMIENTO.predictivo, color: 'white' }} />
        </Box>
      </Box>

      <Calendar
        localizer={localizer}
        culture='es'
        events={events}
        startAccessor="start"
        endAccessor="end"
        selectable
        onSelectSlot={handleSelectSlot}
        onSelectEvent={handleSelectEvent}
        views={['month', 'week', 'day', 'agenda']}
        defaultView="week"
        style={{ background: 'rgb(69, 69, 71)', borderRadius: 8 }}
        date={date}
        onNavigate={(newDate) => setDate(newDate)}
        step={30}
        timeslots={2}
        min={new Date(2026, 0, 1, 6, 0, 0)}
        max={new Date(2026, 0, 1, 22, 0, 0)}
        formats={{
          timeGutterFormat: 'HH:mm',
          dayFormat: (date, culture, localizer) => localizer.format(date, 'eeee dd', culture),
          dayHeaderFormat: (date, culture, localizer) => localizer.format(date, 'eeee dd/MM', culture),
          dayRangeHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'dd MMM', culture)} - ${localizer.format(end, 'dd MMM yyyy', culture)}`,
          monthHeaderFormat: (date, culture, localizer) => localizer.format(date, 'MMMM yyyy', culture),
          agendaDateFormat: (date, culture, localizer) => localizer.format(date, 'eeee dd/MM', culture),
          agendaHeaderFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'dd MMM yyyy', culture)} - ${localizer.format(end, 'dd MMM yyyy', culture)}`,
          eventTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
          agendaTimeRangeFormat: ({ start, end }, culture, localizer) =>
            `${localizer.format(start, 'HH:mm', culture)} - ${localizer.format(end, 'HH:mm', culture)}`,
        }}
        eventPropGetter={event => ({
          className: 'evento-mantenimiento',
          style: {
            backgroundColor: event.color,
            color: '#fff',
            borderRadius: '6px',
            border: 'none',
            opacity: event.raw.estado === 'completado' ? 0.6 : 
                    event.raw.estado === 'cancelado' ? 0.4 : 1
          }
        })}
        messages={{
          date: 'Fecha',
          time: 'Hora',
          event: 'Evento',
          allDay: 'Todo el día',
          week: 'Semana',
          work_week: 'Semana laboral',
          day: 'Día',
          month: 'Mes',
          previous: 'Anterior',
          next: 'Siguiente',
          yesterday: 'Ayer',
          tomorrow: 'Mañana',
          today: 'Hoy',
          agenda: 'Agenda',
          noEventsInRange: 'No hay eventos en este rango',
          showMore: total => `+${total} más`
        }}
      />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingEvent ? 'Editar mantenimiento' : 'Programar nuevo mantenimiento'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          
          {/* Advertencia de conflictos */}
          {form.machineNumber && form.fechaInicio && form.horaInicio && form.fechaFin && form.horaFin && 
           form.estado !== 'cancelado' && form.estado !== 'completado' && (() => {
            const conflicts = checkEventConflict({
              machineNumber: form.machineNumber,
              fechaInicio: `${form.fechaInicio}T${form.horaInicio}:00`,
              fechaFin: `${form.fechaFin}T${form.horaFin}:00`,
              id: editingEvent?.id
            });
            
            if (conflicts.length > 0) {
              const conflictInfo = conflicts.map(c => {
                const start = format(new Date(c.start), 'dd/MM HH:mm');
                const end = format(new Date(c.end), 'HH:mm');
                return `${c.raw.tipoMantenimiento} (${start}-${end})`;
              }).join(', ');
              
              return (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  ⚠️ Conflicto detectado con: {conflictInfo}
                </Alert>
              );
            }
            return null;
          })()}
          
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                select
                label="Máquina"
                value={form.machineNumber}
                onChange={e => handleChange('machineNumber', e.target.value)}
                fullWidth
                required
              >
                <MenuItem value=""><em>Seleccione una máquina</em></MenuItem>
                {maquinas.map(m => (
                  <MenuItem key={m._id} value={m.machineNumber}>
                    Máquina {m.machineNumber}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Box>
                <label style={{ display: 'block', marginBottom: '8px', fontSize: '0.875rem', color: '#666' }}>
                  Tipo de Mantenimiento *
                </label>
                <ToggleButtonGroup
                  value={form.tipoMantenimiento}
                  exclusive
                  onChange={(e, value) => value && handleChange('tipoMantenimiento', value)}
                  fullWidth
                  color="primary"
                >
                  <ToggleButton value="correctivo" sx={{ textTransform: 'none' }}>
                    <BuildIcon sx={{ mr: 1 }} /> Correctivo
                  </ToggleButton>
                  <ToggleButton value="preventivo" sx={{ textTransform: 'none' }}>
                    <WarningIcon sx={{ mr: 1 }} /> Preventivo
                  </ToggleButton>
                  <ToggleButton value="predictivo" sx={{ textTransform: 'none' }}>
                    <TrendingUpIcon sx={{ mr: 1 }} /> Predictivo
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Descripción del mantenimiento"
                value={form.descripcion}
                onChange={e => handleChange('descripcion', e.target.value)}
                fullWidth
                required
                multiline
                rows={3}
                placeholder="Describe las actividades a realizar..."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                label="Técnico asignado"
                value={form.tecnicoAsignado}
                onChange={e => handleChange('tecnicoAsignado', e.target.value)}
                fullWidth
                placeholder="Nombre del técnico (opcional)"
              />
            </Grid>

            {editingEvent && (
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label="Estado"
                  value={form.estado}
                  onChange={e => handleChange('estado', e.target.value)}
                  fullWidth
                >
                  <MenuItem value="programado">Programado</MenuItem>
                  <MenuItem value="en_proceso">En Proceso</MenuItem>
                  <MenuItem value="completado">Completado</MenuItem>
                  <MenuItem value="cancelado">Cancelado</MenuItem>
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de inicio"
                type="date"
                value={form.fechaInicio}
                onChange={e => handleChange('fechaInicio', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hora de inicio"
                type="time"
                value={form.horaInicio}
                onChange={e => handleChange('horaInicio', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Fecha de fin"
                type="date"
                value={form.fechaFin}
                onChange={e => handleChange('fechaFin', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Hora de fin"
                type="time"
                value={form.horaFin}
                onChange={e => handleChange('horaFin', e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>

            {editingEvent && (
              <Grid item xs={12}>
                <TextField
                  label="Observaciones"
                  value={form.observaciones}
                  onChange={e => handleChange('observaciones', e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  placeholder="Observaciones adicionales..."
                />
              </Grid>
            )}
          </Grid>

          {editingEvent && (
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
              <Chip 
                label={form.estado.replace('_', ' ').toUpperCase()} 
                color={getEstadoChipColor(form.estado)}
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {editingEvent && (
            <Button color="error" onClick={handleDelete}>Eliminar</Button>
          )}
          <Button onClick={() => setOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleSave}>Guardar</Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default CalendarioMantenimiento;
